/**
 * Power-up glyphs (06 §5): monochrome silhouettes on a 64-unit grid, stroke
 * 7 units, no fills except the named ones (filled triangle, filled
 * semicircle). Drawn in neutral ink, never in team accent. Also the two
 * team marks (A = square, B = circle) used as a colourblind channel.
 */
import { POWERUPS, powerUpById, type PowerUpDef } from '../../sim/powerups.js'

export type GlyphName = PowerUpDef['glyph'] | 'teamA' | 'teamB' | 'empty'

export const INK = 'rgba(228,234,240,0.95)'

/** Draw `glyph` centred at (cx, cy) at `size` px (the 64-unit grid scaled). */
export function drawGlyph(ctx: CanvasRenderingContext2D, glyph: GlyphName, cx: number, cy: number, size: number, ink: string = INK): void {
  const u = size / 64
  ctx.save()
  ctx.translate(cx - 32 * u, cy - 32 * u)
  ctx.scale(u, u)
  ctx.strokeStyle = ink
  ctx.fillStyle = ink
  ctx.lineWidth = 7
  ctx.lineCap = 'butt'
  ctx.lineJoin = 'miter'
  ctx.beginPath()
  switch (glyph) {
    case 'bars': // Two vertical bars
      ctx.moveTo(22, 10); ctx.lineTo(22, 54); ctx.moveTo(42, 10); ctx.lineTo(42, 54); ctx.stroke(); break
    case 'triangle': // Filled triangle
      ctx.moveTo(32, 8); ctx.lineTo(56, 54); ctx.lineTo(8, 54); ctx.closePath(); ctx.fill(); break
    case 'zigzag': // Three-segment bolt
      ctx.moveTo(14, 10); ctx.lineTo(50, 10); ctx.lineTo(14, 54); ctx.lineTo(50, 54); ctx.stroke(); break
    case 'dashedRing': // Dashed circle
      ctx.setLineDash([9, 7]); ctx.arc(32, 32, 22, 0, Math.PI * 2); ctx.stroke(); ctx.setLineDash([]); break
    case 'halfDisc': // Filled semicircle (flat side left)
      ctx.moveTo(24, 8); ctx.arc(24, 32, 24, -Math.PI / 2, Math.PI / 2); ctx.closePath(); ctx.fill(); break
    case 'hook': // Hooked arrow
      ctx.moveTo(50, 12); ctx.lineTo(50, 40); ctx.arc(36, 40, 14, 0, Math.PI / 2); ctx.lineTo(14, 54)
      ctx.moveTo(26, 42); ctx.lineTo(14, 54); ctx.lineTo(26, 66 - 6); ctx.stroke(); break
    case 'ringDot': // Ring + centre dot
      ctx.arc(32, 32, 22, 0, Math.PI * 2); ctx.stroke(); ctx.beginPath(); ctx.arc(32, 32, 6, 0, Math.PI * 2); ctx.fill(); break
    case 'chevrons': // Double chevron
      ctx.moveTo(10, 12); ctx.lineTo(30, 32); ctx.lineTo(10, 52); ctx.moveTo(32, 12); ctx.lineTo(52, 32); ctx.lineTo(32, 52); ctx.stroke(); break
    case 'gapSquare': // Open square with a gap (reserved Intercept)
      ctx.moveTo(22, 12); ctx.lineTo(12, 12); ctx.lineTo(12, 52); ctx.lineTo(52, 52); ctx.lineTo(52, 12); ctx.lineTo(42, 12); ctx.stroke(); break
    case 'teamA':
      ctx.rect(14, 14, 36, 36); ctx.stroke(); break
    case 'teamB':
      ctx.arc(32, 32, 19, 0, Math.PI * 2); ctx.stroke(); break
    case 'empty':
      break
  }
  ctx.restore()
}

export function glyphForPowerUp(id: string | null | undefined): GlyphName {
  if (!id) return 'empty'
  return powerUpById(id)?.glyph ?? 'empty'
}

/** 14 R9: all glyphs + team marks at 64/40/24/16/8 px, greyscale. */
export function renderGlyphSheet(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  ctx.fillStyle = '#111418'
  ctx.fillRect(0, 0, w, h)
  const sizes = [64, 40, 24, 16, 8]
  const items: { name: string; glyph: GlyphName }[] = [
    ...POWERUPS.map((p) => ({ name: p.reserved ? `${p.id} (reserved)` : p.id, glyph: p.glyph })),
    { name: 'team A', glyph: 'teamA' }, { name: 'team B', glyph: 'teamB' },
  ]
  ctx.font = '12px ui-monospace, monospace'
  ctx.textBaseline = 'middle'
  let y = 40
  for (const it of items) {
    ctx.fillStyle = INK
    ctx.fillText(it.name, 16, y)
    let x = 200
    for (const s of sizes) { drawGlyph(ctx, it.glyph, x + 36, y, s); x += 96 }
    y += 84
  }
  ctx.fillStyle = 'rgba(228,234,240,0.5)'
  ctx.fillText('sizes: 64 / 40 / 24 / 16 / 8 px, neutral ink, greyscale by construction (06 §5)', 16, y)
}
